import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import { Modal } from '@/shared/ui/Modal';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { Review } from '../api/reviewsApi';
import { useReviewActions } from '../hooks/useReviewActions';
import { safetyVerify } from '../utils/safetyVerify';
import { StarRating } from './StarRating';
import styles from './ReviewResponseModal.module.css';

const FLAG_LABELS: Record<string, string> = {
  names_reviewer: 'Mentions the reviewer by name',
  mentions_procedure: 'Mentions a specific clinical procedure',
  confirms_visit: "Confirms the reviewer's visit or treatment",
};

interface Props {
  review: Review | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ReviewResponseModal({ review, isOpen, onClose }: Props) {
  const { post, regenerate } = useReviewActions();
  const [text, setText] = useState('');
  const [hipaaBlocked, setHipaaBlocked] = useState(false);
  const [hipaaFlags, setHipaaFlags] = useState<string[]>([]);

  useEffect(() => {
    setText(review?.draftText ?? '');
    // Only show the warning if a human edit caused the failure.
    // PN-generated drafts are clean by construction (repair loop); held_manual
    // drafts have hipaaPassed=null, not false.
    setHipaaBlocked(review?.hipaaPassed === false && review?.editedByHuman === true);
    setHipaaFlags(review?.hipaaFlags ?? []);
  }, [
    review?.id,
    review?.draftText,
    review?.hipaaPassed,
    review?.hipaaFlags,
    review?.editedByHuman,
  ]);

  if (!review) return null;

  const handleTextChange = (value: string) => {
    setText(value);
    // Run real-time safety check on human edits so violations surface as the
    // user types — not just on post attempt.
    const result = safetyVerify(value, review?.reviewerName ?? null);
    setHipaaBlocked(!result.passed);
    setHipaaFlags(result.flags);
  };

  const handlePost = () => {
    post.mutate({ id: review.id, editedText: text.trim() }, { onSuccess: onClose });
  };

  const handleRegenerate = async () => {
    const updated = await regenerate.mutateAsync(review.id);
    setText(updated.draftText ?? '');
    // PN drafts are clean by construction; only flag if a human edit caused it.
    setHipaaBlocked(updated.hipaaPassed === false && updated.editedByHuman === true);
    setHipaaFlags(updated.hipaaFlags ?? []);
  };

  const regenerateLabel = !review.draftText
    ? 'Generate draft'
    : hipaaBlocked
      ? 'Re-generate'
      : 'Regenerate';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Respond to review"
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRegenerate}
            isLoading={regenerate.isPending}
          >
            <RefreshCw size={14} aria-hidden /> {regenerateLabel}
          </Button>
          <Button
            size="sm"
            onClick={handlePost}
            isLoading={post.isPending}
            disabled={!text.trim() || hipaaBlocked}
          >
            Post reply
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={`${patterns.panelCompact} ${styles.reviewBlock}`}>
          <div className={styles.reviewHead}>
            <span className={styles.reviewer}>{review.reviewerName ?? 'Anonymous'}</span>
            <StarRating rating={review.rating} />
          </div>
          {review.comment ? (
            <p className={styles.reviewComment}>{review.comment}</p>
          ) : (
            <p className={styles.noComment}>
              This patient left a star rating with no written review.
            </p>
          )}
        </div>

        {/* repairAttempts > 0 = a draft failed compliance; 0 = sync never auto-drafted it. */}
        {review.status === 'held_manual' && !review.draftText && review.repairAttempts > 0 && (
          <Callout variant="info" icon={AlertTriangle} title="Auto-draft unavailable">
            A compliant draft couldn't be generated for this review. Write your reply manually
            below.
          </Callout>
        )}

        {hipaaBlocked && (
          <Callout variant="warning" icon={AlertTriangle} title="Compliance warning">
            Your edit may reference patient information and cannot be posted as-is. Remove the
            flagged content before posting:
            <ul className={styles.hipaaList}>
              {hipaaFlags.map((f) => (
                <li key={f}>{FLAG_LABELS[f] ?? f}</li>
              ))}
            </ul>
          </Callout>
        )}

        <div className={styles.labelRow}>
          <label className={patterns.fieldLabel} htmlFor="review-reply-text">
            Your reply
          </label>
          {!hipaaBlocked && text.trim() && (
            <span className={patterns.pillSuccess}>
              <ShieldCheck size={12} aria-hidden /> Compliant
            </span>
          )}
        </div>
        <textarea
          id="review-reply-text"
          className={patterns.textarea}
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          rows={6}
          placeholder="Write a reply…"
        />
        <p className={styles.hint}>
          This reply will be posted publicly to Google on behalf of your practice.
        </p>
      </div>
    </Modal>
  );
}
